// ════════════════════════════════════════════════════════════
//  battle.js — SHATTERPROOF's engine (pure JS, no React, no three)
// ════════════════════════════════════════════════════════════
//  Everything that decides who wins lives here; nothing that draws.
//  The renderer and the HUD only READ this object. Because it imports
//  nothing visual, tools/sim-check.mjs runs hundreds of fights in Node.
//
//  ── THE RULES, as caP'n wrote them (2026-10-15) ─────────────
//  · 3v3. One being per side on the field. You move it; you aim
//    everything with the mouse (free aim — nothing tracks for you).
//  · Every being: its card's Lyt and Lyf, and its own Anima
//    (20 Beb / 30 Champion / 40 Lord). Lyf is the shield; Anima is
//    exposed only while Lyf is down, or by an Art that says so.
//    Lyf mends 1/s once 5 s pass without damage; any damage restarts
//    the wait. Anima is restored only by Arts. Anima 0 = ruined.
//  · KHAO is the Aenarchist's, shared by all three beings. Start (and
//    max) 30. After 5 s without spending, it refills 3/s.
//  · Lyt Attack: every being, power = its Lyt, one per 3 s.
//  · PRIMERA: the dodge, by Ilk, free, 5 s cooldown.
//  · IMPERA: the line's own, card fare, no cooldown. U'hlti: Lord only.
//    TOMES: three slots, card fare, 10 s cooldown. Cast times by level:
//    Lv1 3 s (warned), Lv2 1 s (warned), Lv3 instant.
//  · Transcend: 15 Khao → Champion, 30 Khao → Lord.
//  · Switch: 5 / 7 / 10 Khao for a Beb / Champion / Lord coming in.
//  · Resurrection (caP'n 2026-10-15): every being can be resurrected ONCE
//    per fight (3 per team), fare 3 / 4 / 5 Khao, to the field or the hand.
//    Revives granted by an Art don't count against it.
//
//  ── LAWS THIS FILE KEEPS ────────────────────────────────────
//  · Seeded (mulberry32), never Math.random — same seed, same fight.
//  · Paying your own Anima is VOLUNTARY and can never take you to 0.
//  · Every discrete command is one-shot (the e.repeat law is the
//    input layer's); a command that can't happen says why.
//  · Feel numbers are DIALS at the top.
// ════════════════════════════════════════════════════════════
import { ARTS, TURN, CAST_TIME, TOME_COOLDOWN, TOME_SLOTS, DEFAULT_TOMES, TOME_IDS } from "../data/arts.js";
import { LINES, KIN_MOTION, tierStats, TIER_ORDER, TIER_LEVEL, ANIMA_BY_TIER, imperaOpen } from "../data/roster.js";

// ── DIALS ────────────────────────────────────────────────────
export const TEAM_SIZE = 3;
export const ARENA_R = 9.5;
export const KHAO_START = 30, KHAO_MAX = 30;
export const KHAO_IDLE = 5, KHAO_REGEN = 3;          // seconds unspent → per second after
export const LYF_IDLE = 5, LYF_REGEN = 1;            // seconds unhurt → per second after
export const LYT_COOLDOWN = 3;
export const PRIMERA_COOLDOWN = 5;
export const PRIMERA_REACH = 5;                      // Astraea mark & Dragon leap distance
export const PRIMERA_TIME = 1;                        // every Primera is one second
export const TRANSCEND_KHAO = { champion: 15, lord: 30 };
// ⭐ THE ASCENSION CLOCK (caP'n 2026-10-16: "no one can ascend for the first minute of
// the fight. Then you can go to Champion all you want with all beings, but not Lord.
// Lord has a wait time of 2:30"). Seconds of fight before each tier opens, for everyone.
export const TRANSCEND_OPENS = { champion: 60, lord: 150 };
export const transcendWait = (b, tier) => Math.max(0, (TRANSCEND_OPENS[tier] || 0) - b.t);
export const SWAP_KHAO = { beb: 5, champion: 7, lord: 10 };
export const RES_TALLY = { beb: 1, champion: 1, lord: 1 };   // once per being per fight (per tier = a dial)
export const RES_FARE = { beb: 3, champion: 4, lord: 5 };
export const RES_COOLDOWN = 0;                       // no per-turn limit in Shatterproof (a dial)
export const CARRY_MAX = 40;                         // ⟵ DIAL the most Anima a being can carry into a Climb bout (a Lord's full Anima)
export const ART_REACH = 9;                          // how far an aimed Art can be placed
export const TRANSCEND_TIME = 2.6;                   // real seconds of cinematic
export const NEXT_DELAY = 1.8, AUTO_SEND = 5;
export const BODY_R = 0.75;
export const SHOT = { speed: 18, range: 11, radius: 0.32 };
export const LUNGE = { windup: 0.12, active: 0.2, recover: 0.3, dash: 15, reach: 2.1 };
export const MOVE_WHILE = { windup: 0.45, recover: 0.6 };
export const AI_SPACING = { melee: 3.4, ranged: 6.8 };
// ⭐ THE PUSH (caP'n 2026-10-16: "make sure they fight fast use more resources
// etc so the fight ends closer to 5 minutes"). How hard the AI presses — its
// BEHAVIOUR only; no rule, stat or cost changes. Distinct styles still come
// from the beings it picks.
export const AI_PUSH = {
  reserve: 3,        // Khao it keeps back early in a fight (was 6)
  lateReserve: 0,    // ...and by the bell
  artTry: 0.6,       // chance per thought to look for an Art (was 0.35, random pick)
  artBar: 2,         // an Art must be worth at least this to be cast
  engage: 0.7,       // chance a neutral being closes in to attack (was 0.5)
  kill: true,        // a foe with its Lyf down gets chased, no backing off
  swapAt: 0.25, swapChance: 0.3,   // pulling out a dying being (fades to 0 by the bell)
  heatFrom: 0.3,     // from 30% of the clock on it presses harder, all-in by the bell
  // BANK, THEN BURST: Khao only refills after 5 s unspent, so dribbling it
  // out a bit at a time starves the bar. Once it runs low the AI stops
  // spending until the bar is back up, then spends in a burst.
  bankLow: 10,        // below this it starts saving
  bankHigh: 30,      // ...and saves until it has this much (full is 30)
};
// ⭐ PLAYBOOKS (caP'n 2026-10-16: the League "able to learn and adapt"). A side may
// fight with its own style: a few of the AI_PUSH dials above, changed. Behaviour only —
// no rule, stat or cost. The League's NPCs pick theirs (online/mind.js); a style missing
// or unknown is just AI_PUSH. ⟵ DIALS, every number in here.
export const STYLES = {
  balanced: { name: "Balanced", blurb: "a bit of everything", push: {} },
  brawler: { name: "Brawler", blurb: "in your face, all fight", push: { engage: 0.92, reserve: 1, lateReserve: 0, heatFrom: 0.1, swapChance: 0.1, bankLow: 6, bankHigh: 20 } },
  artist: { name: "Artist", blurb: "lives on its Arts", push: { artTry: 0.92, artBar: 1, engage: 0.5 } },
  banker: { name: "Banker", blurb: "saves up, then spends big", push: { bankLow: 18, bankHigh: 30, reserve: 6, lateReserve: 2 } },
  survivor: { name: "Survivor", blurb: "keeps everyone alive, wins late", push: { engage: 0.6, swapAt: 0.4, swapChance: 0.6, heatFrom: 0.45, artBar: 1.5 } },
};
export const STYLE_IDS = Object.keys(STYLES);
export const pushOf = (style) => ({ ...AI_PUSH, ...((style && STYLES[style] && STYLES[style].push) || {}) });
export const CONSTRUCTS_MAX = 3;
// caP'n 2026-10-16: "Battle's end at 5 minutes" (was 12:00).
export const OVERTIME = 240, RIFT_EVERY = 6, CALL_TIME = 300;
// THE RIFT (beings on the field bleed 1 Anima every RIFT_EVERY s from OVERTIME on) is OFF.
// caP'n 2026-10-16: "get rid of that for now but pin it as a possible mechanic for
// overtime matches later". The code stays, behind this switch.
export const RIFT_ON = false;

function mulberry32(a) {
  const R = function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  R.state = () => a | 0;   // read-only peek for the desync fingerprint (never advances it)
  return R;
}
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const len = (x, z) => Math.sqrt(x * x + z * z);
const wrapPi = (a) => { while (a > Math.PI) a -= 2 * Math.PI; while (a < -Math.PI) a += 2 * Math.PI; return a; };
const ilkOf = (f) => LINES[f.line].ilk;

// ════════════════════════════════════════════════════════════
//  A BEING
// ════════════════════════════════════════════════════════════
function makeBeing(side, idx, line) {
  const b = {
    uid: side * 10 + idx, side, idx, line, tier: "beb",
    lyfBase: 0, lytBase: 0, bonusLyf: 0, bonusLyt: 0,
    lyf: 0, anima: 0, maxAnima: 0,
    lastHurtT: -99, ruined: false, resUsed: 0,
    lytCd: 0, lytCharges: 0, pierceCharges: 0, primeraCd: 0,
    statuses: {}, treasures: {},
    x: 0, z: 0, vx: 0, vz: 0, yaw: side === 0 ? Math.PI : 0,
    state: "bench", stateT: 0, move: null, moveData: null, hitDone: 0,
    leap: null, mark: null, trail: [], steer: null,
    ai: { strafe: 1, strafeT: 0, think: 0 },
    anim: { lean: 0, squash: 0, flash: 0, spin: 0, glow: 0 },
  };
  setTierStats(b, "beb", true);
  return b;
}
function setTierStats(f, tier, fresh) {
  const st = tierStats(f.line, tier);
  f.tier = tier;
  f.lyfBase = st.lyf; f.lytBase = st.lyt;
  f.maxAnima = ANIMA_BY_TIER[tier];
  // caP'n 2026-10-16: an Ascent raises the Anima CAP only ("Only raise the cap") — no free Anima either
  // an Ascent never takes Anima away: a being that walked in over its cap (THE CLIMB's carry) keeps it
  f.anima = fresh ? f.maxAnima : f.anima;
  // caP'n 2026-10-16: "Ascending does not refill Lyf, it increases the cap of course but
  // doesn't grant free refill" — a fresh being (start, resurrection) starts full; an Ascent keeps what it had.
  f.lyf = fresh ? maxLyf(f) : Math.min(maxLyf(f), f.lyf);
}
export const maxLyf = (f) => Math.max(0, f.lyfBase + f.bonusLyf);
export const lytOf = (f) => Math.max(0, f.lytBase + f.bonusLyt + (f.treasures.gauntlets ? 2 : 0));

// ════════════════════════════════════════════════════════════
//  THE BATTLE
// ════════════════════════════════════════════════════════════
// callTime: when THIS fight is called (s). The game's rule is CALL_TIME (5:00); a tournament may carry its
// own (Kings Road is called at 4:00 — caP'n 2026-10-16: "we can even make it 4:00").
// carry (THE CLIMB, 2026-10-18): per side, null (everyone fresh) or one Anima per being, in roster order —
// a being walks in with EXACTLY what it carried out of the last bout (caP'n 2026-10-20: "it should carry" —
// an Ascended being's Anima above its Beb cap comes along too, up to CARRY_MAX); 0 = it starts
// ruined, on the bench, and the fight's one resurrection can bring it back. caP'n 2026-10-17: only Anima
// carries; everything else (tier, Khao, Lyf, cooldowns, the resurrection) starts fresh.
export function createBattle({ teams, tomes = [null, null], seed = 1, auto = [false, true], names = ["You", "Rival"], callTime = CALL_TIME, styles = [null, null], carry = [null, null] }) {
  const R = mulberry32(seed);
  const b = {
    t: 0, seed, R, seq: 0, events: [], winner: null, over: false, callTime,
    timeScale: 1, cinematic: null,
    projectiles: [], zones: [], casts: [], constructs: [], nextId: 1,
    sides: [0, 1].map((s) => ({
      side: s, name: names[s], style: (styles && styles[s]) || null, push: pushOf(styles && styles[s]),
      khao: KHAO_START, khaoMax: KHAO_MAX, khaoBonus: 0, lastSpendT: -99, lastAnimaGainT: -99,
      roster: teams[s].slice(0, TEAM_SIZE).map((line, i) => makeBeing(s, i, line)),
      active: -1, pendingNext: 0, auto: !!auto[s], wantSend: null, swapLock: 0, resCd: 0,
      tomes: (tomes[s] || pickTomes(R)).slice(0, TOME_SLOTS).map((id) => ({ id, cd: 0 })),
      aim: { x: 0, z: s === 0 ? -4 : 4 },
    })),
  };
  for (const s of b.sides) {
    const c = carry && carry[s.side];
    if (!Array.isArray(c)) continue;
    s.roster.forEach((r, i) => {
      const v = Number(c[i]);
      if (c[i] == null || !Number.isFinite(v)) return;
      if (v <= 0) { r.anima = 0; r.ruined = true; r.state = "bench"; }        // quietly: no ruin event, it happened last bout
      else r.anima = Math.min(CARRY_MAX, Math.max(1, Math.round(v)));
    });
    if (s.roster.every((r) => r.ruined)) s.roster.forEach((r) => { r.ruined = false; r.anima = r.maxAnima; });   // never start a fight already lost
  }
  for (const s of b.sides) sendIn(b, s, Math.max(0, s.roster.findIndex((r) => !r.ruined)), true);
  emit(b, { type: "start" });
  return b;
}
function pickTomes(R) {
  const pool = TOME_IDS.slice(), out = [];
  while (out.length < TOME_SLOTS && pool.length) out.push(pool.splice(Math.floor(R() * pool.length), 1)[0]);
  return out.length ? out : DEFAULT_TOMES.slice();
}

function emit(b, e) {
  e.seq = ++b.seq; e.t = b.t;
  b.events.push(e);
  if (b.events.length > 300) b.events.splice(0, b.events.length - 300);
}
export const activeOf = (b, side) => { const s = b.sides[side]; return s.active >= 0 ? s.roster[s.active] : null; };
const foeOf = (b, f) => activeOf(b, 1 - f.side);
const say = (b, f, text, color = "#fff", o = {}) => emit(b, { type: "text", uid: f.uid, x: f.x, z: f.z, text, color, ...o });

function sendIn(b, s, idx, opening = false) {
  const f = s.roster[idx];
  if (!f || f.ruined) return false;
  s.active = idx;
  const dir = s.side === 0 ? 1 : -1;
  if (opening || (f.x === 0 && f.z === 0)) { f.x = (b.R() - 0.5) * 2; f.z = dir * 6; }
  else { f.x = clamp(f.x, -3, 3); f.z = dir * 6; }
  f.vx = 0; f.vz = -dir * 2; f.yaw = s.side === 0 ? Math.PI : 0;
  f.state = "enter"; f.stateT = 0.85;
  f.move = null; f.leap = null; f.mark = null; f.trail = []; f.steer = null;
  f.ai = { strafe: 1, strafeT: 0, think: 0 };
  emit(b, { type: "enter", side: s.side, uid: f.uid, line: f.line, tier: f.tier, x: f.x, z: f.z });
  // Hope: +1 Anima whenever a Time being enters combat
  if (ilkOf(f) === "Time") for (const sd of b.sides) for (const r of sd.roster) {
    if (!r.ruined && r.statuses.hope) gainAnima(b, r, 1);
  }
  return true;
}

// ════════════════════════════════════════════════════════════
//  COMMANDS
// ════════════════════════════════════════════════════════════
//  { type:"aim", x, z }         where the cursor points on the field
//  { type:"steer", x, z }       analog intent, null to release
//  { type:"lyt" }               the Lyt Attack, at the aim
//  { type:"primera" }           the dodge, at the aim
//  { type:"impera", i }         Impera I/II/III (i = 0,1,2)
//  { type:"uhlti" }             the Lord's U'hlti
//  { type:"tome", slot }        a Tome (slot 0,1,2)
//  { type:"transcend" }         pay Khao, ascend
//  { type:"swap", idx }         switch the active being (Khao)
//  { type:"send", idx }         after a ruin, choose who goes next
//  { type:"resurrect", idx, to: "field"|"hand" }
//  { type:"auto", on }          hand this side to the AI
//  { type:"style", id }         the side's playbook (STYLES) from now on —
//                               how its AI spends and spaces. LYRAEI PLAYS
//                               (2026-10-17): a partner on Auto calls her
//                               game plan with it. Behaviour only, no rule,
//                               stat or cost; unknown id = the default AI.
export function command(b, side, cmd) {
  if (b.over) return false;
  const s = b.sides[side];
  if (cmd.type === "auto") { s.auto = !!cmd.on; return true; }
  if (cmd.type === "style") {
    const id = cmd.id && Object.prototype.hasOwnProperty.call(STYLES, cmd.id) ? cmd.id : null;   // own keys only: "constructor" is not a plan
    if (id === s.style) return false;
    s.style = id; s.push = pushOf(id);
    emit(b, { type: "style", side, style: id });
    return true;
  }
  if (cmd.type === "aim") { s.aim.x = cmd.x; s.aim.z = cmd.z; return true; }
  if (cmd.type === "send") { const n = s.roster[cmd.idx]; if (!n || n.ruined || s.active >= 0) return false; s.wantSend = cmd.idx; return true; }
  if (cmd.type === "resurrect") return tryResurrect(b, s, cmd.idx, cmd.to);
  const f = activeOf(b, side);
  if (!f) return false;
  switch (cmd.type) {
    case "steer": f.steer = cmd.x == null ? null : { x: cmd.x, z: cmd.z }; return true;
    case "lyt": return tryLyt(b, f);
    case "primera": return tryPrimera(b, f);
    case "impera": return tryArt(b, f, LINES[f.line].impera[cmd.i], { impera: cmd.i });
    case "uhlti": return tryArt(b, f, LINES[f.line].uhlti, { uhlti: true });
    case "tome": { const t = s.tomes[cmd.slot]; return t ? tryArt(b, f, t.id, { tome: t }) : false; }
    case "transcend": return tryTranscend(b, f);
    case "swap": return trySwap(b, s, f, cmd.idx);
    default: return false;
  }
}

const imbues = (a) => a.effects.some((e) => e.lyt || e.maxLyf || e.treasure);
const busy = (f) => ["windup", "active", "stagger", "leap", "transcend", "enter", "ruined"].includes(f.state);
export const canAct = (f) => f && !busy(f);

// what a call would cost and whether it can happen now — the HUD asks this
export function artReady(b, f, id, o = {}) {
  const a = ARTS[id]; if (!a) return { ok: false, why: "none" };
  const s = b.sides[f.side];
  if (o.impera != null && !imperaOpen(o.impera, f.tier)) return { ok: false, why: "locked", a };
  if (o.uhlti && f.tier !== "lord") return { ok: false, why: "locked", a };
  if (o.tome && o.tome.cd > 0) return { ok: false, why: "cooldown", a };
  if (s.khao + 1e-6 < a.khao) return { ok: false, why: "khao", a };
  if (a.anima && f.anima - a.anima < 1) return { ok: false, why: "anima", a };
  // a stat Impera or a Treasure is IMBUED to its being once (Terror's
  // Imbue) — a second cast can't stack it forever
  if (imbues(a) && f.imbued && f.imbued[id]) return { ok: false, why: "imbued", a };
  if (f.state === "transcend" || f.state === "enter" || f.state === "ruined") return { ok: false, why: "busy", a };
  return { ok: true, a };
}

function spendKhao(b, s, n) { if (n > 0) { s.khao -= n; s.lastSpendT = b.t; } }

// ── the Lyt Attack ───────────────────────────────────────────
function tryLyt(b, f) {
  if (busy(f)) return false;
  if (f.lytCd > 0 && f.lytCharges <= 0) return false;
  if (f.lytCd > 0) f.lytCharges--; else f.lytCd = LYT_COOLDOWN * (f.treasures.sword ? 1 : 1);
  const s = b.sides[f.side];
  const ax = s.aim.x - f.x, az = s.aim.z - f.z;
  const yaw = len(ax, az) > 0.05 ? Math.atan2(ax, az) : f.yaw;
  f.yaw = yaw;
  const pierce = f.pierceCharges > 0 ? (f.pierceCharges--, true) : false;
  const form = LINES[f.line].lyt;
  f.moveData = { yaw, pierce, cast: {} };
  if (form === "shot") {
    f.move = { kind: "shot", windup: 0.12, active: 0.1, recover: 0.2 };
  } else {
    f.move = { kind: "lunge", windup: LUNGE.windup, active: LUNGE.active, recover: LUNGE.recover };
  }
  f.state = "windup"; f.stateT = f.move.windup; f.hitDone = 0;
  emit(b, { type: "windup", uid: f.uid, side: f.side, name: LINES[f.line].lytName, slot: 0, x: f.x, z: f.z });
  return true;
}

// ── the Primera ──────────────────────────────────────────────
function tryPrimera(b, f) {
  if (f.primeraCd > 0) return false;
  if (["leap", "transcend", "enter", "ruined"].includes(f.state)) return false;
  const s = b.sides[f.side];
  const ilk = ilkOf(f);
  const ax = s.aim.x - f.x, az = s.aim.z - f.z, ad = len(ax, az) || 1;
  f.primeraCd = PRIMERA_COOLDOWN;
  if (f.state === "windup" || f.state === "active" || f.state === "recover" || f.state === "stagger") { f.state = "idle"; f.move = null; }
  if (ilk === "Phantom") {
    // stay exactly where you are (you can move) — Intangible for 1 s
    f.statuses.intangible = { t: PRIMERA_TIME };
    emit(b, { type: "primera", kind: "phase", uid: f.uid, x: f.x, z: f.z });
  } else if (ilk === "Astraea") {
    // mark a spot within reach; one second later you are there
    const d = Math.min(PRIMERA_REACH, ad);
    const tx = f.x + (ax / ad) * d, tz = f.z + (az / ad) * d;
    const p = clampPoint(tx, tz);
    f.mark = { x: p.x, z: p.z, t: PRIMERA_TIME };
    emit(b, { type: "primera", kind: "mark", uid: f.uid, x: f.x, z: f.z, tx: p.x, tz: p.z });
  } else if (ilk === "Time") {
    // back to where you stood one second ago
    const back = f.trail.length ? f.trail[0] : { x: f.x, z: f.z };
    emit(b, { type: "primera", kind: "rewind", uid: f.uid, x: f.x, z: f.z, tx: back.x, tz: back.z });
    f.x = back.x; f.z = back.z; f.vx = f.vz = 0; f.trail = [];
  } else {
    // Dragon: leap toward the aim; untouchable until it lands
    const p = clampPoint(f.x + (ax / ad) * PRIMERA_REACH, f.z + (az / ad) * PRIMERA_REACH);
    f.leap = { fx: f.x, fz: f.z, tx: p.x, tz: p.z, t: 0 };
    f.state = "leap"; f.stateT = PRIMERA_TIME; f.yaw = Math.atan2(ax, az);
    emit(b, { type: "primera", kind: "leap", uid: f.uid, x: f.x, z: f.z, tx: p.x, tz: p.z });
  }
  return true;
}
function clampPoint(x, z, R = ARENA_R) { const r = len(x, z); return r > R ? { x: x / r * R, z: z / r * R } : { x, z }; }

// ── Arts: Impera, U'hlti, Tomes ──────────────────────────────
function tryArt(b, f, id, o = {}) {
  const r = artReady(b, f, id, o);
  if (!r.ok) {
    if (r.why === "khao") say(b, f, "NEED KHAO", "#7CE7FF", { small: true });
    if (r.why === "anima") say(b, f, "NOT ENOUGH ANIMA", "#FF4D8D", { small: true });
    if (r.why === "imbued") say(b, f, "ALREADY IMBUED", "#f2c14e", { small: true });
    return false;
  }
  const a = r.a, s = b.sides[f.side];
  spendKhao(b, s, a.khao);
  if (a.anima) { f.anima = Math.max(1, f.anima - a.anima); emit(b, { type: "anima", uid: f.uid, delta: -a.anima, x: f.x, z: f.z, self: true }); }
  if (o.tome) o.tome.cd = TOME_COOLDOWN;
  if (imbues(a)) { f.imbued = f.imbued || {}; f.imbued[id] = true; }
  // where it lands: the cursor, never further than an Art's reach
  let x = f.x, z = f.z;
  if (a.target === "area") {
    const ax = s.aim.x - f.x, az = s.aim.z - f.z, d = len(ax, az);
    const k = d > ART_REACH ? ART_REACH / d : 1;
    const p = clampPoint(f.x + ax * k, f.z + az * k);
    x = p.x; z = p.z;
  }
  const delay = CAST_TIME[a.level] ?? 0;
  const cast = { id: b.nextId++, art: id, side: f.side, uid: f.uid, x, z, r: a.r, t: delay, tMax: delay, color: colorOf(f), level: a.level };
  emit(b, { type: "cast", uid: f.uid, side: f.side, art: id, name: a.name, kind: a.kind, level: a.level, x, z, r: a.r, target: a.target, delay, ult: a.kind === "uhlti" });
  if (a.kind === "uhlti") {
    b.cinematic = { kind: "ult", uid: f.uid, until: b.t + 0.9 };
    emit(b, { type: "ult", uid: f.uid, side: f.side, name: a.name, line: f.line });
  }
  if (a.target === "area" && delay > 0) {
    // the warning — the renderer draws this exactly like a strike from above
    b.zones.push({ id: cast.id, side: f.side, x, z, r: a.r, t: delay, tMax: delay, color: cast.color, warn: true, art: id });
  }
  f.anim.glow = 1;
  if (delay <= 0) resolveArt(b, cast); else b.casts.push(cast);
  return true;
}

function beingsOnField(b) { return [activeOf(b, 0), activeOf(b, 1)].filter((x) => x && !x.ruined); }
function ownerOf(b, cast) { for (const r of b.sides[cast.side].roster) if (r.uid === cast.uid) return r; return null; }

function resolveArt(b, cast) {
  const a = ARTS[cast.art];
  const caster = ownerOf(b, cast);
  const s = b.sides[cast.side], foeSide = b.sides[1 - cast.side];
  const foe = activeOf(b, 1 - cast.side);
  // who is caught: an area hits the adversary's being inside it; a
  // global Art reaches across the whole field
  let caught = [];
  if (a.target === "area") { if (foe && !foe.ruined && len(foe.x - cast.x, foe.z - cast.z) < cast.r + BODY_R) caught = [foe]; }
  else if (a.target === "global") caught = foe ? [foe] : [];
  // immunities: Tomeproof against Tomes, Destined against Impera & U'hltis
  caught = caught.filter((t) => {
    if (a.kind === "tome" && t.statuses.tomeproof) { say(b, t, "TOMEPROOF", "#f2c14e", { small: true }); return false; }
    if ((a.kind === "impera" || a.kind === "uhlti") && t.statuses.destined) { say(b, t, "DESTINED", "#ffd24a", { small: true }); return false; }
    if (isUntouchable(t) && !(t.statuses.intangible && a.effects.some((e) => e.dimAnimaIfIntangible))) { say(b, t, "PHASED", "#B388FF", { small: true }); return false; }
    return true;
  });
  emit(b, { type: "art", art: cast.art, name: a.name, kind: a.kind, shape: a.shape, x: cast.x, z: cast.z, r: cast.r, target: a.target, color: cast.color, uid: cast.uid, side: cast.side, caught: caught.length, level: a.level, ult: a.kind === "uhlti" });
  if (a.target === "area") emit(b, { type: "scorch", x: cast.x, z: cast.z, r: cast.r * 0.7, color: cast.color, crater: a.kind === "uhlti" });
  if (a.kind === "uhlti") emit(b, { type: "shake", amp: 0.8, dur: 0.5 });

  const team = s.roster.filter((r) => !r.ruined);
  const count = (per) => {
    if (!per) return 1;
    if (per === "dragon") return team.filter((r) => ilkOf(r) === "Dragon").length;
    if (per === "astraea") return team.filter((r) => ilkOf(r) === "Astraea").length;
    if (per === "dragonField") return beingsOnField(b).filter((r) => ilkOf(r) === "Dragon").length;
    if (per === "timeField") return beingsOnField(b).filter((r) => ilkOf(r) === "Time").length;
    if (per === "otherAntdroid") return team.filter((r) => r.line === "antdroid" && r !== caster).length;
    return 1;
  };
  const run = (effects) => {
    for (const e of effects) {
      if (e.needs === "otherChampionTime" && !team.some((r) => r !== caster && r.tier === "champion" && ilkOf(r) === "Time")) { if (caster) say(b, caster, "NO OTHER LV2 TIME BEING", "#8a7aa0", { small: true }); continue; }
      if (e.needs === "gainedAnima" && b.t - s.lastAnimaGainT > TURN) { if (caster) say(b, caster, "NO ANIMA GAINED THIS TURN", "#8a7aa0", { small: true }); continue; }
      if (e.lytToAnima != null || e.lytHit != null) {
        const amt = (caster ? lytOf(caster) : 0) + (e.lytToAnima ?? e.lytHit);
        for (const t of caught) harm(b, t, amt, { toAnima: e.lytToAnima != null, by: caster, art: true });
      }
      if (e.dimLyf) for (const t of caught) dimLyf(b, t, e.dimLyf, caster);
      if (e.dimAnima) for (const t of caught) harm(b, t, e.dimAnima, { toAnima: true, by: caster, art: true });
      if (e.dimAnimaIfIntangible) for (const t of caught) {
        if (t.statuses.intangible) { harm(b, t, e.dimAnimaIfIntangible, { toAnima: true, by: caster, art: true, pierceVeil: true }); say(b, t, "SCALES PIERCE THE VEIL", "#ff8aa0"); }
        else say(b, t, "NOT INTANGIBLE", "#8a7aa0", { small: true });
      }
      if (e.lyfOrAnima) for (const t of caught) { if (t.lyf > 0) dimLyf(b, t, e.lyfOrAnima[0], caster); else harm(b, t, e.lyfOrAnima[1], { toAnima: true, by: caster, art: true }); }
      if (e.status) {
        const targets = e.on === "self" ? (caster ? [caster] : []) : e.on === "team" ? team : caught;
        for (const t of targets) applyStatus(b, t, { id: e.status, dur: e.dur, charges: e.charges });
      }
      if (e.lytCharges) {
        const n = e.lytCharges * (e.per ? count(e.per) : 1);
        const who = e.team ? team : caster ? [caster] : [];
        for (const t of who) { t.lytCharges += n; if (e.pierce) t.pierceCharges += n; }
        if (caster) say(b, caster, n ? `+${n} LYT ATTACK` : "NO OTHER ANTDROID", n ? "#ffe9a0" : "#8a7aa0", { small: !n });
      }
      if (e.khao) gainKhao(b, s, e.khao, caster);
      if (e.foeKhao) gainKhao(b, foeSide, e.foeKhao, foe);
      if (e.lyt && caster) { const n = e.lyt * count(e.per); caster.bonusLyt += n; say(b, caster, `+${n} LYT`, "#ffe9a0"); }
      if (e.maxLyf && caster) { const n = e.maxLyf * count(e.per); caster.bonusLyf += n; caster.lyf = Math.min(maxLyf(caster), caster.lyf + n); say(b, caster, `+${n} LYF`, "#7fe8d8"); }
      if (e.ruinNonBeings) {
        // the cards say "1": the nearest enemy construct in the area, else one Treasure
        let ruined = 0, near = null, nd = 1e9;
        for (const c of b.constructs) { const d = len(c.x - cast.x, c.z - cast.z); if (c.side !== cast.side && !c.dead && d < cast.r + c.r && d < nd) { near = c; nd = d; } }
        if (near) { ruinConstruct(b, near); ruined++; }
        else for (const t of caught) { const k = Object.keys(t.treasures)[0]; if (k) { delete t.treasures[k]; ruined++; say(b, t, "TREASURE RUINED", "#e8453c"); break; } }
        if (!ruined && e.orKhao) gainKhao(b, s, e.orKhao, caster);
      }
      if (e.cleanse) for (const t of caught) { t.statuses = {}; t.treasures = {}; say(b, t, "NULLIFIED", "#7fe8d8"); }
      if (e.ruinOverLyt) for (const t of beingsOnField(b)) if (lytOf(t) > e.ruinOverLyt && !t.statuses.destined && !isUntouchable(t)) { say(b, t, "NONE MORE THAN I", "#ff9b2e", { big: true }); ruin(b, t, caster); }
      if (e.ruinBenches) {
        for (const sd of b.sides) { if (sd.active < 0) continue; for (const r of sd.roster) if (!r.ruined && sd.active !== r.idx && !shielded(b, r)) { ruinBench(b, r); } }
        emit(b, { type: "shake", amp: 1, dur: 0.8 });
      }
      if (e.bothAnima) for (const t of beingsOnField(b)) gainAnima(b, t, e.bothAnima);
      if (e.coin) {
        const won = b.R() < 0.5;
        if (caster) say(b, caster, won ? "HEADS!" : "TAILS…", won ? "#ffe08a" : "#8a7aa0");
        run(won ? e.coin.win : e.coin.lose);
      }
      if (e.dice) {
        const d1 = 1 + Math.floor(b.R() * 6), d2 = 1 + Math.floor(b.R() * 6);
        if (caster) say(b, caster, d1 + d2 === 2 ? "SNAKE EYES!!" : `${d1} + ${d2}`, d1 + d2 === 2 ? "#ff4d8d" : "#ffe08a", { big: d1 + d2 === 2 });
        for (const t of caught) { if (d1 + d2 === 2) harm(b, t, 12, { toAnima: true, by: caster, art: true }); else dimLyf(b, t, d1 + d2, caster); }
      }
      if (e.reviveIlk) {
        const maxI = TIER_ORDER.indexOf(e.maxTier);
        let n = 0;
        for (const r of s.roster) {
          if (n >= e.count) break;
          if (r.ruined && ilkOf(r) === e.reviveIlk && TIER_ORDER.indexOf(r.tier) <= maxI) { revive(b, s, r, "hand", true); n++; }
        }
        if (!n && caster) say(b, caster, "NONE IN THE NECROPOLIS", "#8a7aa0", { small: true });
      }
      if (e.construct) spawnConstruct(b, cast, e.construct);
      if (e.treasure && caster) { caster.treasures[e.treasure.id] = { ...e.treasure, t: e.treasure.chargeEvery || e.treasure.phaseEvery || 0, ready: true }; say(b, caster, ARTS[cast.art].name.toUpperCase(), "#f2c14e"); }
    }
  };
  run(a.effects);
}

function gainKhao(b, s, n, who) {
  s.khao = Math.min(s.khaoMax, s.khao + n);
  if (who) say(b, who, `+${n} KHAO`, "#7CE7FF");
}
function gainAnima(b, f, n) {
  if (!f || f.ruined) return;
  const before = f.anima;
  f.anima = Math.max(f.anima, Math.min(f.maxAnima, f.anima + n));   // a heal never pulls an over-cap being down to its cap
  if (f.anima > before) { b.sides[f.side].lastAnimaGainT = b.t; emit(b, { type: "heal", uid: f.uid, x: f.x, z: f.z, amt: f.anima - before, anima: true }); }
}

// ── constructs: the Tomes that stand on the field ────────────
function spawnConstruct(b, cast, c) {
  const mine = b.constructs.filter((k) => k.side === cast.side && !k.dead);
  if (mine.length >= CONSTRUCTS_MAX) ruinConstruct(b, mine[0]);
  const k = { id: b.nextId++, side: cast.side, kind: c.kind, art: cast.art, x: cast.x, z: cast.z, r: c.taunt ? 1.1 : 0.8,
    lyf: Math.max(1, c.lyf || 0), every: c.every || 0, t: c.every || 0, give: c.give, uses: c.uses ?? Infinity, cap: c.cap ?? Infinity, gave: 0,
    taunt: !!c.taunt, intangibleT: c.intangibleFor || 0, color: cast.color, dead: false };
  b.constructs.push(k);
  emit(b, { type: "construct", id: k.id, kind: k.kind, x: k.x, z: k.z, side: k.side, color: k.color });
}
function ruinConstruct(b, k) {
  if (k.dead) return;
  k.dead = true;
  if (k.give && k.give.maxKhao) { const s = b.sides[k.side]; s.khaoMax = Math.max(KHAO_MAX, s.khaoMax - k.gave); s.khao = Math.min(s.khao, s.khaoMax); }
  emit(b, { type: "construct_ruined", id: k.id, x: k.x, z: k.z, color: k.color });
}
function tickConstructs(b, dt) {
  for (const k of b.constructs) {
    if (k.dead) continue;
    if (k.intangibleT > 0) k.intangibleT -= dt;
    if (!k.every || !k.give) continue;
    k.t -= dt;
    if (k.t > 0) continue;
    k.t = k.every;
    const s = b.sides[k.side], f = activeOf(b, k.side);
    if (k.give.anima && f) gainAnima(b, f, k.give.anima);
    if (k.give.maxKhao && k.gave < k.cap) { s.khaoMax += k.give.maxKhao; k.gave += k.give.maxKhao; emit(b, { type: "text", x: k.x, z: k.z, text: "+1 MAX KHAO", color: "#7CE7FF" }); }
    k.uses--;
    if (k.uses <= 0) ruinConstruct(b, k);
  }
  b.constructs = b.constructs.filter((k) => !k.dead);
}

// ── Transcend, switch, resurrect ─────────────────────────────
function tryTranscend(b, f) {
  if (f.tier === "lord" || busy(f)) return false;
  const next = TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1];
  const s = b.sides[f.side], cost = TRANSCEND_KHAO[next];
  const wait = transcendWait(b, next);
  if (wait > 0) { say(b, f, `${next.toUpperCase()} OPENS AT ${Math.floor(TRANSCEND_OPENS[next] / 60)}:${String(TRANSCEND_OPENS[next] % 60).padStart(2, "0")}`, "#7CE7FF", { small: true }); return false; }
  if (s.khao < cost) { say(b, f, `NEED ${cost} KHAO`, "#7CE7FF", { small: true }); return false; }
  spendKhao(b, s, cost);
  f.state = "transcend"; f.stateT = TRANSCEND_TIME; f.vx = f.vz = 0; f.move = null;
  b.cinematic = { kind: "transcend", uid: f.uid, until: b.t + TRANSCEND_TIME * 0.92 };
  emit(b, { type: "transcend_start", uid: f.uid, side: f.side, line: f.line, from: f.tier, to: next, x: f.x, z: f.z });
  return true;
}
function finishTranscend(b, f) {
  const from = f.tier, next = TIER_ORDER[TIER_ORDER.indexOf(from) + 1];
  setTierStats(f, next, false);
  f.state = "idle";
  emit(b, { type: "transcend", uid: f.uid, side: f.side, line: f.line, from, to: next, x: f.x, z: f.z });
  emit(b, { type: "shake", amp: 0.8, dur: 0.6 });
  emit(b, { type: "scorch", x: f.x, z: f.z, r: 3.2, color: colorOf(f), crater: true, rune: true });
}
function trySwap(b, s, f, idx) {
  const n = s.roster[idx];
  if (!n || n.ruined || idx === s.active || s.swapLock > 0 || busy(f)) return false;
  const cost = SWAP_KHAO[n.tier];
  if (s.khao < cost) { say(b, f, `SWAP NEEDS ${cost} KHAO`, "#7CE7FF", { small: true }); return false; }
  spendKhao(b, s, cost);
  emit(b, { type: "recall", side: s.side, uid: f.uid, x: f.x, z: f.z });
  f.state = "bench"; f.move = null;
  s.swapLock = 1;
  sendIn(b, s, idx);
  return true;
}
function tryResurrect(b, s, idx, to = "hand") {
  const r = s.roster[idx];
  if (!r || !r.ruined) return false;
  if (r.resUsed >= RES_TALLY[r.tier]) return false;
  if (s.resCd > 0) return false;
  const fare = RES_FARE[r.tier];
  if (s.khao < fare) return false;
  const cur = activeOf(b, s.side);
  if (to === "field" && cur && !cur.ruined && busy(cur)) return false;   // never cut a Transcend or a leap short
  spendKhao(b, s, fare);
  s.resCd = RES_COOLDOWN;
  revive(b, s, r, to);
  return true;
}
function revive(b, s, r, to, byArt = false) {
  if (!byArt) r.resUsed++;   // an Art's revive is free of the once-per-fight rite
  r.ruined = false; r.statuses = {}; r.treasures = {}; r.lytCharges = 0; r.pierceCharges = 0;
  r.bonusLyf = 0; r.bonusLyt = 0; r.imbued = {};
  setTierStats(r, r.tier, true);
  r.lastHurtT = -99; r.state = "bench";
  emit(b, { type: "resurrect", side: s.side, uid: r.uid, line: r.line, tier: r.tier, to });
  const cur = activeOf(b, s.side);
  if (to === "field" || !cur) {
    if (cur && !cur.ruined) { emit(b, { type: "recall", side: s.side, uid: cur.uid, x: cur.x, z: cur.z }); cur.state = "bench"; cur.move = null; }
    sendIn(b, s, r.idx);
  }
}

// ════════════════════════════════════════════════════════════
//  THE STEP
// ════════════════════════════════════════════════════════════
const FIXED = 1 / 120;
export function step(b, realDt) {
  if (b.over) { b.t += realDt; b.cinematic = null; b.timeScale = 1; return; }
  realDt = Math.min(realDt, 0.1);
  let scale = 1;
  if (b.cinematic) {
    if (b.t >= b.cinematic.until) b.cinematic = null;
    else scale = b.cinematic.kind === "transcend" ? 0.12 : b.cinematic.kind === "ult" ? 0.45 : 0.4;
  }
  b.timeScale = scale;
  let remaining = realDt;
  while (remaining > 1e-6) {
    const h = Math.min(FIXED, remaining);
    remaining -= h;
    tick(b, h * scale, h);
    if (b.over) break;
  }
}

// ⭐ THE FIXED TICK (2026-10-16, online play). step() above eats whatever
// frame time it is handed, so two screens slice the same second
// differently and drift apart. stepFixed() is ONE tick of exactly 1/120 s,
// cinematic slow-motion decided per tick, so N calls are the same fight on
// every machine. online/lockstep.js drives fights this way; offline play
// keeps step(). (Same engine, same rules — only the slicing differs.)
export const TICK = FIXED;
export function stepFixed(b) {
  if (b.over) { b.t += FIXED; b.cinematic = null; b.timeScale = 1; return; }
  let scale = 1;
  if (b.cinematic) {
    if (b.t >= b.cinematic.until) b.cinematic = null;
    else scale = b.cinematic.kind === "transcend" ? 0.12 : b.cinematic.kind === "ult" ? 0.45 : 0.4;
  }
  b.timeScale = scale;
  tick(b, FIXED * scale, FIXED);
}

// a cheap fingerprint of the fight, for the desync check
export function stateHash(b) {
  let h = 2166136261 >>> 0;
  const mix = (v) => { h = Math.imul(h ^ (Math.round(v * 1000) | 0), 16777619) >>> 0; };
  mix(b.t); mix(b.R.state ? b.R.state() / 1000 : 0); mix(b.seq);
  for (const s of b.sides) {
    mix(s.khao); mix(s.active); mix(s.auto ? 1 : 0);
    for (const t of s.tomes) mix(t.cd);
    for (const r of s.roster) {
      mix(r.x); mix(r.z); mix(r.vx); mix(r.vz); mix(r.yaw); mix(r.anima); mix(r.lyf); mix(r.ruined ? 1 : 0);
      mix(r.lytCd); mix(r.primeraCd); mix(Object.keys(r.statuses).length);
    }
  }
  mix(b.projectiles.length); mix(b.casts.length); mix(b.constructs.length);
  return h.toString(36);
}

function tick(b, dt, realDt) {
  b.t += realDt;
  for (const s of b.sides) {
    // Khao: 5 s unspent, then 3 per second
    if (b.t - s.lastSpendT >= KHAO_IDLE) s.khao = Math.min(s.khaoMax, s.khao + KHAO_REGEN * dt);
    s.swapLock = Math.max(0, s.swapLock - dt);
    s.resCd = Math.max(0, s.resCd - dt);
    for (const t of s.tomes) t.cd = Math.max(0, t.cd - dt);
    if (s.active < 0 || s.roster[s.active].ruined) {
      s.pendingNext -= realDt;
      const alive = s.roster.filter((r) => !r.ruined);
      if (!alive.length) continue;
      const want = s.wantSend;
      if (s.pendingNext <= 0 && want != null && s.roster[want] && !s.roster[want].ruined) { sendIn(b, s, want); s.wantSend = null; }
      else if (s.pendingNext <= -(s.auto ? 0 : AUTO_SEND)) { sendIn(b, s, alive[0].idx); s.wantSend = null; }
    }
    // the bench keeps mending its Lyf too
    for (const r of s.roster) if (!r.ruined && s.active !== r.idx) regen(b, r, dt);
  }
  const A = activeOf(b, 0), B = activeOf(b, 1);
  for (const f of [A, B]) if (f && !f.ruined) {
    if (f.state !== "transcend") think(b, f, dt);
    updateBeing(b, f, f.state === "transcend" ? realDt : dt);
  }
  if (A && B && !A.ruined && !B.ruined) separate(A, B);
  updateProjectiles(b, dt);
  updateCasts(b, dt);
  tickConstructs(b, dt);
  checkClash(b, A, B);
  checkEnd(b, realDt);
}

function regen(b, f, dt) {
  if (b.t - f.lastHurtT >= LYF_IDLE && f.lyf < maxLyf(f)) f.lyf = Math.min(maxLyf(f), f.lyf + LYF_REGEN * dt);
}

function checkEnd(b, dt) {
  if (RIFT_ON && b.t > OVERTIME) {
    if (!b.overtime) { b.overtime = true; b.riftT = RIFT_EVERY; emit(b, { type: "overtime" }); emit(b, { type: "shake", amp: 0.4, dur: 1 }); }
    b.riftT -= dt;
    if (b.riftT <= 0) { b.riftT = RIFT_EVERY; for (const f of beingsOnField(b)) harm(b, f, 1, { toAnima: true, rift: true }); }
  }
  if (b.t > b.callTime && !b.over) {
    const j = judge(b);
    b.over = true; b.winner = j.winner;
    emit(b, { type: "end", winner: b.winner, why: "called", by: j.by });
    return;
  }
  const outs = b.sides.map((s) => s.roster.every((r) => r.ruined));
  if ((outs[0] || outs[1]) && !b.over) {
    b.over = true;
    if (outs[0] && outs[1]) { const j = judge(b); b.winner = j.winner; emit(b, { type: "end", winner: b.winner, why: "called", by: j.by }); }
    else { b.winner = outs[0] ? 1 : 0; emit(b, { type: "end", winner: b.winner, why: "ruined" }); }
  }
}

// ⭐ THE CALL (caP'n 2026-10-16): when a fight is called, the winner is, in order —
//   1. more beings still standing (not ruined, not in the Necropolis)
//   2. more Anima left across those beings
//   3. FEWER resurrections used (a return costs you here; an Art's revive doesn't count)
//   4. (caP'n hasn't ruled past this) more Lyf left, then a coin from the fight's seed
export const CALL_ORDER = ["beings", "anima", "resurrections", "lyf", "coin"];
export function judge(b) {
  const tally = b.sides.map((s) => {
    const up = s.roster.filter((r) => !r.ruined);
    return {
      beings: up.length,
      anima: up.reduce((a, r) => a + r.anima, 0),
      resurrections: s.roster.reduce((a, r) => a + (r.resUsed || 0), 0),
      lyf: up.reduce((a, r) => a + r.lyf, 0),
    };
  });
  const [A, B] = tally, eq = (x, y) => Math.abs(x - y) < 1e-6;
  if (A.beings !== B.beings) return { winner: A.beings > B.beings ? 0 : 1, by: "beings", tally };
  if (!eq(A.anima, B.anima)) return { winner: A.anima > B.anima ? 0 : 1, by: "anima", tally };
  if (A.resurrections !== B.resurrections) return { winner: A.resurrections < B.resurrections ? 0 : 1, by: "resurrections", tally };
  if (!eq(A.lyf, B.lyf)) return { winner: A.lyf > B.lyf ? 0 : 1, by: "lyf", tally };
  return { winner: b.R() < 0.5 ? 0 : 1, by: "coin", tally };
}

function updateBeing(b, f, dt) {
  const kin = KIN_MOTION[LINES[f.line].kin];
  const foe = foeOf(b, f);
  const s = b.sides[f.side];
  f.lytCd = Math.max(0, f.lytCd - dt);
  f.primeraCd = Math.max(0, f.primeraCd - dt);
  for (const k in f.statuses) { const st = f.statuses[k]; st.t -= dt; if (st.t <= 0) delete f.statuses[k]; }
  // treasures that act on a timer
  const sw = f.treasures.sword; if (sw) { sw.t -= dt; if (sw.t <= 0) { sw.t = sw.chargeEvery; f.lytCharges = Math.min(f.lytCharges + 1, 2); } }
  const bt = f.treasures.boots; if (bt && !bt.ready) { bt.t -= dt; if (bt.t <= 0) bt.ready = true; }
  regen(b, f, dt);
  // the trail Time rewinds along (one second of positions)
  f.trail.push({ x: f.x, z: f.z, t: b.t });
  while (f.trail.length && b.t - f.trail[0].t > PRIMERA_TIME) f.trail.shift();
  // the Astraea mark lands
  if (f.mark) { f.mark.t -= dt; if (f.mark.t <= 0) { emit(b, { type: "primera", kind: "arrive", uid: f.uid, x: f.x, z: f.z, tx: f.mark.x, tz: f.mark.z }); f.x = f.mark.x; f.z = f.mark.z; f.vx = f.vz = 0; f.mark = null; } }
  for (const k in f.anim) f.anim[k] *= Math.exp(-dt * 7);
  if (f.hitstop > 0) { f.hitstop -= dt; return; }

  f.stateT -= dt;
  switch (f.state) {
    case "enter":
      f.vx *= Math.exp(-dt * 5); f.vz *= Math.exp(-dt * 5);
      if (f.stateT <= 0) f.state = "idle";
      break;
    case "windup":
      drive(f, kin, dt, MOVE_WHILE.windup * (f.move.kind === "lunge" ? 0.35 : 1));
      f.anim.glow = Math.max(f.anim.glow, 1 - Math.max(0, f.stateT) / (f.move.windup || 0.01));
      f.yaw = f.moveData.yaw;
      if (f.stateT <= 0) beginActive(b, f);
      break;
    case "active":
      if (f.move.kind === "lunge") {
        f.vx *= Math.exp(-dt * 3); f.vz *= Math.exp(-dt * 3);
        if (foe && !f.hitDone && !foe.ruined && len(foe.x - f.x, foe.z - f.z) < BODY_R * 2 + 0.55) { f.hitDone = 1; lytHit(b, f, foe); f.vx *= 0.15; f.vz *= 0.15; }
        lungeConstructs(b, f);
      } else drive(f, kin, dt, MOVE_WHILE.windup);
      if (f.stateT <= 0) { f.state = "recover"; f.stateT = f.move.recover; }
      break;
    case "recover":
      drive(f, kin, dt, MOVE_WHILE.recover);
      if (f.stateT <= 0) { f.state = "idle"; f.move = null; }
      break;
    case "stagger":
      f.vx *= Math.exp(-dt * 4); f.vz *= Math.exp(-dt * 4);
      if (f.stateT <= 0) f.state = "idle";
      break;
    case "leap": {
      // the Dragon's leap: an arc over one second, untouchable
      const L = f.leap;
      L.t = Math.min(1, L.t + dt / PRIMERA_TIME);
      const e = L.t < 0.5 ? 2 * L.t * L.t : 1 - Math.pow(-2 * L.t + 2, 2) / 2;
      f.x = L.fx + (L.tx - L.fx) * e; f.z = L.fz + (L.tz - L.fz) * e; f.vx = f.vz = 0;
      if (L.t >= 1) { f.state = "idle"; f.leap = null; emit(b, { type: "primera", kind: "land", uid: f.uid, x: f.x, z: f.z }); emit(b, { type: "shake", amp: 0.25, dur: 0.2 }); }
      return;
    }
    case "transcend":
      f.vx = f.vz = 0; f.anim.glow = 1;
      if (f.stateT <= 0) finishTranscend(b, f);
      break;
    case "idle": default: {
      const sp = drive(f, kin, dt, 1);
      // FREE AIM: a human's being faces the cursor; the AI faces its foe
      let face = f.yaw;
      if (!s.auto) { const ax = s.aim.x - f.x, az = s.aim.z - f.z; if (len(ax, az) > 0.2) face = Math.atan2(ax, az); }
      else if (foe) face = Math.atan2(foe.x - f.x, foe.z - f.z);
      const v = len(f.vx, f.vz);
      f.yaw = turnToward(f.yaw, face, dt * 12);
      f.anim.lean = Math.max(f.anim.lean, clamp(v / sp, 0, 1) * 0.6);
    }
  }
  f.x += f.vx * dt; f.z += f.vz * dt;
  const r = len(f.x, f.z);
  if (r > ARENA_R) {
    const nx = f.x / r, nz = f.z / r;
    f.x = nx * ARENA_R; f.z = nz * ARENA_R;
    const vn = f.vx * nx + f.vz * nz;
    if (vn > 0) { f.vx -= vn * nx * 1.6; f.vz -= vn * nz * 1.6; }
  }
}

function drive(f, kin, dt, share) {
  let wx = 0, wz = 0;
  const want = f.steer || f.ai.want;
  if (want) { wx = want.x; wz = want.z; }
  const wl = len(wx, wz);
  if (wl > 1) { wx /= wl; wz /= wl; }
  const sp = kin.speed * (f.statuses.intangible ? 1.15 : 1);
  const k = 1 - Math.exp(-kin.accel * dt / 4);
  f.vx += (wx * sp * share - f.vx) * k;
  f.vz += (wz * sp * share - f.vz) * k;
  return sp;
}
function turnToward(cur, target, k) { return cur + wrapPi(target - cur) * Math.min(1, k); }

function separate(A, B) {
  if (A.statuses.intangible || B.statuses.intangible || A.state === "leap" || B.state === "leap") return;
  const dx = B.x - A.x, dz = B.z - A.z, l = len(dx, dz), min = BODY_R * 2;
  if (l < min && l > 1e-4) { const p = (min - l) / 2, nx = dx / l, nz = dz / l; A.x -= nx * p; A.z -= nz * p; B.x += nx * p; B.z += nz * p; }
}

function beginActive(b, f) {
  const m = f.move, yaw = f.moveData.yaw;
  const dx = Math.sin(yaw), dz = Math.cos(yaw);
  f.state = "active"; f.stateT = m.active;
  if (m.kind === "lunge") {
    f.vx = dx * LUNGE.dash; f.vz = dz * LUNGE.dash; f.anim.squash = 1; f.anim.lean = 1;
    emit(b, { type: "lunge", uid: f.uid, x: f.x, z: f.z, color: colorOf(f) });
  } else {
    b.projectiles.push({ id: b.nextId++, side: f.side, owner: f.uid, x: f.x + dx * 0.9, z: f.z + dz * 0.9, y: 0.9,
      vx: dx * SHOT.speed, vz: dz * SHOT.speed, r: SHOT.radius, life: SHOT.range / SHOT.speed, shape: f.line === "dg0" ? "halo" : "star",
      color: colorOf(f), pierce: f.moveData.pierce });
    f.vx -= dx * 2; f.vz -= dz * 2;
    emit(b, { type: "fire", uid: f.uid, x: f.x, z: f.z, color: colorOf(f), count: 1 });
  }
}

// a lunge through a construct ruins it
function lungeConstructs(b, f) {
  for (const k of b.constructs) if (!k.dead && k.side !== f.side && k.intangibleT <= 0 && len(k.x - f.x, k.z - f.z) < k.r + BODY_R) { hurtConstruct(b, k, lytOf(f)); f.hitDone = 1; }
}
function hurtConstruct(b, k, n) {
  k.lyf -= Math.max(1, n);
  emit(b, { type: "hit", uid: null, x: k.x, z: k.z, dmg: Math.max(1, n), color: k.color, construct: true });
  if (k.lyf <= 0) ruinConstruct(b, k);
}

function updateProjectiles(b, dt) {
  for (const p of b.projectiles) {
    p.life -= dt;
    if (p.life <= 0) continue;
    p.x += p.vx * dt; p.z += p.vz * dt;
    // a D'uhmdumn takes the shot first — "must ruin this card before attacking another"
    let blocked = false;
    for (const k of b.constructs) if (!k.dead && k.side !== p.side && k.intangibleT <= 0 && len(k.x - p.x, k.z - p.z) < k.r + p.r) {
      const owner = ownerById(b, p.owner); hurtConstruct(b, k, owner ? lytOf(owner) : 1); p.life = -1; blocked = true; break;
    }
    if (blocked) continue;
    const foe = activeOf(b, 1 - p.side);
    // a lunge in full swing bats a shot back — once
    if (foe && !foe.ruined && foe.state === "active" && foe.move && foe.move.kind === "lunge" && !p.deflected && len(foe.x - p.x, foe.z - p.z) < BODY_R + p.r + 0.45) {
      const back = activeOf(b, p.side);
      const aim = back ? Math.atan2(back.x - p.x, back.z - p.z) : Math.atan2(-p.vx, -p.vz);
      const sp = len(p.vx, p.vz) * 1.15;
      p.side = foe.side; p.owner = foe.uid; p.color = colorOf(foe); p.deflected = true; p.pierce = false;
      p.vx = Math.sin(aim) * sp; p.vz = Math.cos(aim) * sp; p.life = Math.max(p.life, 1.0);
      say(b, foe, "DEFLECTED!", "#F2C14E");
      emit(b, { type: "clash", x: p.x, z: p.z, big: false, color: p.color, color2: "#ffffff", art: "deflect" });
      continue;
    }
    if (!p.phased && foe && !foe.ruined && foe.state !== "bench" && len(foe.x - p.x, foe.z - p.z) < p.r + BODY_R) {
      const owner = ownerById(b, p.owner);
      const res = owner ? lytHit(b, owner, foe, { pierce: p.pierce, shot: p }) : "none";
      if (res !== "phased") p.life = -1; else p.phased = true;
    }
    if (len(p.x, p.z) > ARENA_R + 3) p.life = -1;
  }
  b.projectiles = b.projectiles.filter((p) => p.life > 0);
}
function ownerById(b, uid) { for (const s of b.sides) for (const r of s.roster) if (r.uid === uid) return r; return null; }

// pending casts: the warning ticks down, then the Art appears
function updateCasts(b, dt) {
  for (const c of b.casts) {
    c.t -= dt;
    if (c.t <= 0 && !c.done) { c.done = true; resolveArt(b, c); }
  }
  b.casts = b.casts.filter((c) => !c.done);
  for (const z of b.zones) { z.t -= dt; if (z.t <= 0) z.done = true; }
  b.zones = b.zones.filter((z) => !z.done || z.t > -0.4);
}

function checkClash(b, A, B) {
  if (!A || !B || A.ruined || B.ruined) return;
  const d = len(A.x - B.x, A.z - B.z);
  if (A.state === "active" && B.state === "active" && A.move && B.move && A.move.kind === "lunge" && B.move.kind === "lunge" && !A.hitDone && !B.hitDone && d < BODY_R * 2 + 0.7) {
    const nx = (B.x - A.x) / (d || 1), nz = (B.z - A.z) / (d || 1);
    const mA = KIN_MOTION[LINES[A.line].kin].mass, mB = KIN_MOTION[LINES[B.line].kin].mass;
    A.vx = -nx * 9 / mA; A.vz = -nz * 9 / mA; B.vx = nx * 9 / mB; B.vz = nz * 9 / mB;
    A.hitDone = B.hitDone = 1; A.hitstop = B.hitstop = 0.14;
    A.state = B.state = "recover"; A.stateT = B.stateT = 0.35;
    emit(b, { type: "clash", x: (A.x + B.x) / 2, z: (A.z + B.z) / 2, big: true, color: colorOf(A), color2: colorOf(B) });
    emit(b, { type: "shake", amp: 0.55, dur: 0.3 });
  }
}

// ════════════════════════════════════════════════════════════
//  HARM — every way a being is hurt goes through here
// ════════════════════════════════════════════════════════════
//  A Lyt Attack: its power lands on Lyf first; while Lyf stands, the
//  hit only wears the shield (no spill-over). Lyf at 0 → Anima.
function lytHit(b, atk, def, o = {}) {
  if (def.ruined || def.state === "bench") return "none";
  if (isUntouchable(def)) { say(b, def, "PHASED", "#B388FF", { small: true }); return "phased"; }
  // Boots of Wing Walking: the wielder slips one hit, then they recharge
  if (def.treasures.boots && def.treasures.boots.ready) { def.treasures.boots.ready = false; def.treasures.boots.t = def.treasures.boots.phaseEvery; def.statuses.intangible = { t: 1 }; say(b, def, "WING WALK", "#B388FF"); return "phased"; }
  if (def.statuses.warded) { if (--def.statuses.warded.charges <= 0) delete def.statuses.warded; say(b, def, "NULLIFIED", "#7fe8d8"); emit(b, { type: "ward", uid: def.uid, x: def.x, z: def.z }); return "warded"; }
  if (def.statuses.nullLyt) { delete def.statuses.nullLyt; say(b, def, "NULLIFIED", "#ffb04a"); emit(b, { type: "ward", uid: def.uid, x: def.x, z: def.z }); return "warded"; }
  const benched = atk.state === "bench" || atk.ruined;
  if (def.statuses.fate && !benched) {
    delete def.statuses.fate;
    if (b.R() < 0.5) { say(b, def, "FATE — REFLECTED!", "#ffe08a", { big: true }); harm(b, atk, lytOf(atk), { by: def }); return "reflected"; }
    say(b, def, "FATE — TAILS", "#8a7aa0", { small: true });
  }
  // a Lyt Attack on a Thriving-in-Chaos being costs the attacker
  if (def.statuses.reprisal && !benched) harm(b, atk, 1, { toAnima: true, by: def, quiet: true });
  if (atk.statuses.nightmare && !benched) harm(b, atk, 1, { toAnima: true, by: def, quiet: true });
  const pierce = !!(o.pierce || atk.statuses.piercing);
  const dealt = harm(b, def, lytOf(atk), { toAnima: pierce, by: atk, lyt: true });
  if (atk.statuses.scalpel) harm(b, def, 1, { toAnima: true, by: atk, quiet: true });
  if (atk.treasures.blade) for (const r of b.sides[def.side].roster) if (!r.ruined && (r === def || !shielded(b, r))) dimLyf(b, r, 1, atk, true);
  return dealt ? "hit" : "none";
}

// Natural Lyt: while the holder stands on the field, its benched
// teammates cannot be touched ("must be ruined before your adversary
// can attack another")
function shielded(b, r) { const a = activeOf(b, r.side); return !!(a && a !== r && !a.ruined && a.statuses.bulwark); }
function isUntouchable(f) { return f.statuses.intangible || f.state === "leap" || f.state === "transcend" || f.state === "enter"; }

// the core: n to Lyf (no spill) or straight to Anima
function harm(b, def, n, o = {}) {
  if (def.ruined || n <= 0) return 0;
  if (!o.pierceVeil && !o.rift && isUntouchable(def) && !o.quiet) return 0;
  // Natural Lyt: while its holder stands, the rest of its team can't be touched
  def.lastHurtT = b.t;
  let toAnima = o.toAnima || def.lyf <= 0;
  let where;
  if (!toAnima) {
    def.lyf = Math.max(0, def.lyf - n); where = "lyf";
    if (def.lyf <= 0) emit(b, { type: "text", uid: def.uid, x: def.x, z: def.z, text: "EXPOSED", color: "#FF4D8D", small: true });
  } else {
    if (def.statuses.absorb) { gainAnima(b, def, n); say(b, def, "WALL OF LYT", "#ffe9a0", { small: true }); return 0; }
    def.anima = Math.max(0, def.anima - n); where = "anima";
  }
  const heavy = n >= 6;
  if (!o.quiet) {
    const from = o.by ? Math.atan2(def.x - o.by.x, def.z - o.by.z) : 0;
    const kin = KIN_MOTION[LINES[def.line].kin];
    if (o.lyt || o.art) { def.vx += Math.sin(from) * (2 + n * 0.4) / kin.mass; def.vz += Math.cos(from) * (2 + n * 0.4) / kin.mass; }
    if (o.lyt) { def.hitstop = heavy ? 0.1 : 0.06; if (o.by) o.by.hitstop = Math.max(o.by.hitstop || 0, 0.05); }
    if (o.lyt && (def.state === "idle" || def.state === "recover")) { def.state = "stagger"; def.stateT = heavy ? 0.35 : 0.18; }
    def.anim.flash = 1; def.anim.spin = heavy ? 1 : 0.4;
  }
  emit(b, { type: "hit", uid: def.uid, by: o.by && o.by.uid, x: def.x, z: def.z, dmg: n, where, heavy, tick: !!o.quiet, color: o.by ? colorOf(o.by) : "#e8453c" });
  if (heavy && !o.quiet) emit(b, { type: "shake", amp: 0.3, dur: 0.2 });
  if (def.anima <= 0) ruin(b, def, o.by);
  return n;
}
function dimLyf(b, def, n, by, quiet) {
  if (def.ruined || (isUntouchable(def) && !quiet)) return;
  def.lastHurtT = b.t;
  const before = def.lyf;
  def.lyf = Math.max(0, def.lyf - n);
  if (before > 0) emit(b, { type: "hit", uid: def.uid, by: by && by.uid, x: def.x, z: def.z, dmg: Math.round(before - def.lyf), where: "lyf", tick: !!quiet, color: by ? colorOf(by) : "#fff" });
  if (before > 0 && def.lyf <= 0) emit(b, { type: "text", uid: def.uid, x: def.x, z: def.z, text: "EXPOSED", color: "#FF4D8D", small: true });
}

function applyStatus(b, f, st) {
  if (!f || f.ruined) return;
  f.statuses[st.id] = { t: st.dur, charges: st.charges };
  emit(b, { type: "status", uid: f.uid, x: f.x, z: f.z, status: st.id });
}

// a benched being ruined in place: same cleanup as ruin(), no field change
function ruinBench(b, r) {
  r.anima = 0; r.ruined = true; r.state = "bench"; r.treasures = {}; r.statuses = {}; r.imbued = {}; r.bonusLyf = 0; r.bonusLyt = 0;
  for (const p of b.projectiles) if (p.owner === r.uid) p.life = -1;
  for (const c of b.casts) if (c.uid === r.uid) { c.done = true; for (const z of b.zones) if (z.id === c.id) { z.done = true; z.t = -1; } }
  emit(b, { type: "ruined", uid: r.uid, side: r.side, line: r.line, tier: r.tier, bench: true });
}
function ruin(b, f, by) {
  if (f.ruined) return;
  f.anima = 0; f.ruined = true; f.state = "ruined"; f.move = null; f.leap = null; f.mark = null;
  f.treasures = {}; f.statuses = {}; f.imbued = {}; f.bonusLyf = 0; f.bonusLyt = 0;
  const s = b.sides[f.side];
  emit(b, { type: "ruined", uid: f.uid, side: f.side, line: f.line, tier: f.tier, x: f.x, z: f.z, by: by && by.uid });
  emit(b, { type: "shake", amp: 0.6, dur: 0.5 });
  b.cinematic = { kind: "ruin", uid: f.uid, until: b.t + 1.1 };
  if (s.active === f.idx) { s.pendingNext = NEXT_DELAY; s.active = -1; s._lastRuined = f.uid; }
  for (const p of b.projectiles) if (p.owner === f.uid) p.life = -1;
  for (const c of b.casts) if (c.uid === f.uid) { c.done = true; for (const z of b.zones) if (z.id === c.id) { z.done = true; z.t = -1; } }    // its pending Arts (and their warnings) die with it
}

// ════════════════════════════════════════════════════════════
//  THE BRAIN — drives the rival, and your side on Auto (G)
// ════════════════════════════════════════════════════════════
//  Spacing, not a charge: hold the gap, commit to land a Lyt Attack,
//  back off. It aims like a player (leads a moving target), dodges
//  with its own Ilk's Primera, and spends Khao with a reserve.
function think(b, f, dt) {
  const s = b.sides[f.side];
  const ai = f.ai;
  if (!s.auto) { ai.want = null; return; }
  const foe = foeOf(b, f);
  const P = s.push || AI_PUSH;   // this side's playbook (STYLES), or the default
  // HEAT: 0 early, climbing to 1 by the bell — it presses harder as the clock runs
  const heat = clamp((b.t / b.callTime - P.heatFrom) / (1 - P.heatFrom), 0, 1);
  const reserve = P.reserve + (P.lateReserve - P.reserve) * heat;
  // bank, then burst (the whole side shares one bar, so the side decides)
  if (s.khao < P.bankLow) s.saving = true;
  else if (s.khao >= Math.min(s.khaoMax, P.bankHigh)) s.saving = false;
  ai.strafeT -= dt;
  if (ai.strafeT <= 0) { ai.strafe = b.R() < 0.5 ? -1 : 1; ai.strafeT = 1 + b.R() * 2; }
  if (!foe || foe.ruined) { ai.want = { x: -f.x * 0.2, z: -f.z * 0.2 }; return; }
  const dx = foe.x - f.x, dz = foe.z - f.z, d = len(dx, dz) || 1, nx = dx / d, nz = dz / d;
  const melee = LINES[f.line].lyt === "lunge";
  // THE KILL: a foe with its Lyf down is open — go get it
  const open = P.kill && foe.lyf <= 0;
  const spacing = (melee ? AI_SPACING.melee : AI_SPACING.ranged) * (open && !melee ? 0.8 : 1) * (1 - 0.2 * heat);
  ai.modeT = (ai.modeT || 0) - dt;
  if (!ai.mode || ai.modeT <= 0) { ai.mode = open && melee ? "engage" : "neutral"; ai.modeT = 0.5 + b.R() * 1.1; }
  // where it wants to be
  let radial, strafe = ai.strafe * 0.8;
  if (ai.mode === "engage") { radial = 1; strafe *= 0.3; }
  else if (ai.mode === "retreat") { radial = -0.9; strafe *= 1.1; }
  else { radial = clamp((d - spacing) * 0.55, -1, 1); if (!melee && d < 4 * (1 - 0.3 * heat)) radial = -1; }
  // step out of any warning it is standing in
  let flee = null;
  for (const z of b.zones) if (z.warn && z.side !== f.side && len(f.x - z.x, f.z - z.z) < z.r + BODY_R + 0.3) flee = z;
  const rim = len(f.x, f.z) / ARENA_R, rl = len(f.x, f.z) || 1, inward = rim > 0.78 ? (rim - 0.78) * 5 : 0;
  ai.want = {
    x: nx * radial - nz * strafe - (f.x / rl) * inward + (flee ? (f.x - flee.x) * 2 : 0),
    z: nz * radial + nx * strafe - (f.z / rl) * inward + (flee ? (f.z - flee.z) * 2 : 0),
  };
  // aim: lead the target the way a player would
  const lead = melee ? 0.1 : d / SHOT.speed * 0.8;
  s.aim.x = foe.x + foe.vx * lead; s.aim.z = foe.z + foe.vz * lead;

  ai.think -= dt;
  if (ai.think > 0) return;
  ai.think = 0.1 + b.R() * 0.15;

  // DODGE: a warning about to land on it, an incoming lunge, a shot close by
  const warn = b.zones.find((z) => z.warn && z.side !== f.side && z.t < 0.7 && len(f.x - z.x, f.z - z.z) < z.r + BODY_R);
  const shot = b.projectiles.find((p) => p.side !== f.side && len(p.x - f.x, p.z - f.z) < 3);
  const lunge = foe.state === "windup" && foe.move && foe.move.kind === "lunge" && d < 3.5;
  if ((warn || shot || lunge) && f.primeraCd <= 0 && b.R() < 0.55) {
    // aim the dodge AWAY from the danger, sideways
    const ox = warn ? warn.x : shot ? shot.x : foe.x, oz = warn ? warn.z : shot ? shot.z : foe.z;
    const ax = f.x - ox, az = f.z - oz, al = len(ax, az) || 1;
    s.aim.x = f.x + (ax / al) * 5 - (az / al) * 2 * ai.strafe; s.aim.z = f.z + (az / al) * 5 + (ax / al) * 2 * ai.strafe;
    if (tryPrimera(b, f)) return;
  }
  if (busy(f)) return;
  if (s.saving) { lytAttack(b, f, ai, foe, d, melee, open, heat); return; }

  // ascend when it can afford it with room to spare
  if (f.tier !== "lord") { const nt = TIER_ORDER[TIER_ORDER.indexOf(f.tier) + 1], c = TRANSCEND_KHAO[nt]; if (transcendWait(b, nt) <= 0 && s.khao >= Math.min(c + reserve, s.khaoMax) && d > 3.5) { tryTranscend(b, f); return; } }   // Lord costs a full bar: the cushion can't be on top of it
  // bring back the fallen
  if (s.resCd <= 0) for (const r of s.roster) if (r.ruined && r.resUsed < RES_TALLY[r.tier] && s.khao >= RES_FARE[r.tier] + reserve * 2) { tryResurrect(b, s, r.idx, "hand"); return; }
  // switch out a being about to fall (less and less as the bell nears)
  if (f.anima <= f.maxAnima * P.swapAt && s.swapLock <= 0) {
    const better = s.roster.find((r) => !r.ruined && r !== f && r.anima > f.anima * 2);
    if (better && s.khao >= SWAP_KHAO[better.tier] + reserve && b.R() < P.swapChance * (1 - heat)) { trySwap(b, s, f, better.idx); return; }
  }
  // the U'hlti, when it can pay with a margin
  if (f.tier === "lord") {
    const id = LINES[f.line].uhlti, a = ARTS[id];
    if (s.khao >= a.khao && f.anima > a.anima + 6) {
      const at = a.target === "area" ? artAim(f, foe, a) : null;
      if (!at || at.p > 0.3) { if (at) { s.aim.x = at.x; s.aim.z = at.z; } if (tryArt(b, f, id, { uhlti: true })) return; }
    }
  }
  // an Impera or a Tome — the one worth the Khao right now, not a random one
  if (b.R() < P.artTry + 0.3 * heat) {
    let best = null;
    const consider = (id, o) => {
      const a = ARTS[id];
      if (!a || s.khao - a.khao < reserve || !artReady(b, f, id, o).ok) return;
      if (a.anima && f.anima - a.anima < Math.max(6, f.maxAnima * 0.3)) return;   // never bleed itself dry paying an Art's Anima
      if (b.casts.some((c) => c.uid === f.uid && c.art === id)) return;          // the last one hasn't even landed yet
      const v = artWorth(b, f, foe, a, d, heat);
      if (v.score <= 0) return;
      const score = v.score * (0.75 + b.R() * 0.5) / (1 + a.khao * 0.08);   // a little taste in it, cheaper is nicer
      if (!best || score > best.score) best = { id, o, a, at: v.at, score };
    };
    LINES[f.line].impera.forEach((id, i) => consider(id, { impera: i }));
    s.tomes.forEach((t) => consider(t.id, { tome: t }));
    if (best && best.score >= P.artBar) {
      if (best.a.effects.some((e) => e.construct)) { s.aim.x = f.x - nx * 2; s.aim.z = f.z - nz * 2; }   // build behind itself
      else if (best.at) { s.aim.x = best.at.x; s.aim.z = best.at.z; }
      if (tryArt(b, f, best.id, best.o)) return;
    }
  }
  lytAttack(b, f, ai, foe, d, melee, open, heat);
}

// the Lyt Attack: commit and land it, then back off (unless it's going for the kill)
function lytAttack(b, f, ai, foe, d, melee, open, heat) {
  if (f.lytCd <= 0 || f.lytCharges > 0) {
    const reach = melee ? LUNGE.reach + 1.2 : SHOT.range * 0.85;
    // don't swing into a being that can't be touched — wait out the phase, hit as it ends
    const phased = foe.statuses.intangible ? foe.statuses.intangible.t : ["leap", "enter", "transcend"].includes(foe.state) ? foe.stateT : 0;
    if (phased > (melee ? LUNGE.windup + 0.05 : d / SHOT.speed + 0.12)) return;
    if (d < reach) {
      if (tryLyt(b, f)) {
        const stay = (open || f.lytCharges > 0 || b.R() < heat * 0.7) && melee;
        if (stay) { ai.mode = "engage"; ai.modeT = 0.8; } else { ai.mode = "retreat"; ai.modeT = (0.6 + b.R() * 0.7) * (1 - 0.5 * heat); }
      }
    }
    else if (ai.mode === "neutral" && b.R() < (b.sides[f.side].push || AI_PUSH).engage + 0.25 * heat) { ai.mode = "engage"; ai.modeT = 1.8; }
  }
}

// WHERE TO PUT AN AIMED ART, and how likely it is to catch anyone.
// An instant Art goes right on them. One with a warning gets walked out of
// — unless they're against the wall: then the warning sits just inside
// them, and the only way out is the way it can't go.
function artAim(f, foe, a) {
  const delay = CAST_TIME[a.level] ?? 0;
  const rr = len(foe.x, foe.z);
  let x, z, p;
  if (delay <= 0) { x = foe.x; z = foe.z; p = 0.9; }
  else if (rr > ARENA_R * 0.7) {
    const k = Math.max(0, rr - Math.min(a.r * 0.55, 1.4)) / rr;
    x = foe.x * k; z = foe.z * k; p = delay <= 1 ? 0.5 : 0.2;
  } else {
    const lead = Math.min(delay, 1) * 0.5;
    x = foe.x + foe.vx * lead; z = foe.z + foe.vz * lead;
    p = delay <= 1 ? (busy(foe) ? 0.35 : 0.15) : 0.03;
  }
  if (len(x - f.x, z - f.z) > ART_REACH) p = 0;   // out of reach: it would land short
  return { x, z, p };
}

// HOW MUCH AN ART IS WORTH RIGHT NOW (0 = don't). Rough on purpose — it
// only has to tell a good spend from a wasted one.
const AI_SHARP = new Set(["scalpel", "piercing"]);
const danger = (b, f, foe, d) => b.projectiles.some((p) => p.side !== f.side && len(p.x - f.x, p.z - f.z) < 4) ||
  (foe.state === "windup" && d < 4) || b.zones.some((z) => z.warn && z.side !== f.side && len(f.x - z.x, f.z - z.z) < z.r + BODY_R);
function artWorth(b, f, foe, a, d, heat = 0) {
  const s = b.sides[f.side], fs = b.sides[1 - f.side];
  const at = a.target === "area" ? artAim(f, foe, a) : null;
  const hitP = a.target === "area" ? at.p : a.target === "global" ? 1 : 0;
  const lyt = lytOf(f);
  let hurt = 0, self = 0;
  const team = s.roster.filter((r) => !r.ruined);
  for (const e of a.effects) {
    // the same conditions resolveArt checks — don't pay for an effect that will fizzle
    if (e.needs === "otherChampionTime" && !team.some((r) => r !== f && r.tier === "champion" && ilkOf(r) === "Time")) continue;
    if (e.needs === "gainedAnima" && b.t - s.lastAnimaGainT > TURN) continue;
    if (e.per === "otherAntdroid" && !team.some((r) => r.line === "antdroid" && r !== f)) continue;
    if (e.lytToAnima != null) hurt += (lyt + e.lytToAnima) * 1.5;
    if (e.lytHit != null) hurt += (lyt + e.lytHit) * (foe.lyf > 0 ? 1 : 1.5);
    if (e.dimLyf) hurt += foe.lyf > 0 ? Math.min(e.dimLyf, foe.lyf) : 0;
    if (e.dimAnima) hurt += e.dimAnima * 1.5;
    if (e.lyfOrAnima) hurt += foe.lyf > 0 ? e.lyfOrAnima[0] : e.lyfOrAnima[1] * 1.5;
    if (e.dice) hurt += 7;
    if (e.dimAnimaIfIntangible && foe.statuses.intangible) hurt += e.dimAnimaIfIntangible * 1.5;
    if (e.cleanse && (Object.keys(foe.statuses).length || Object.keys(foe.treasures).length)) hurt += 4;
    if (e.status) {
      if (e.on === "foe") hurt += foe.statuses[e.status] ? 0 : 3;
      else if (f.statuses[e.status]) self -= 99;                       // already has it: a wasted cast
      else if (AI_SHARP.has(e.status)) self += d < 6 ? 4 : 1;          // sharpens its attacks
      else if (e.status === "intangible") self += (CAST_TIME[a.level] ?? 0) === 0 && danger(b, f, foe, d) ? 6 : -99;   // a dodge — only worth it if it arrives in time
      else if (e.status === "tomeproof") self += 1 * (1 - heat);
      else self += (f.lyf <= 0 || f.anima < f.maxAnima * 0.6 ? 3 : 0.5) * (1 - 0.6 * heat);   // guards: worth it when hurt
    }
    if (e.lytCharges) self += lyt * e.lytCharges * (d < 5 ? 1 : 0.5);
    if (e.lyt) self += 6;
    if (e.maxLyf) self += 3;
    if (e.treasure) self += 5;
    if (e.khao) self += e.khao * 0.5;
    if (e.construct) self += b.constructs.filter((c) => c.side === f.side && !c.dead).length < CONSTRUCTS_MAX ? 4 : 0;
    if (e.coin) { const net = (list) => list.reduce((n, x) => n + (x.khao || 0) - (x.foeKhao || 0) + (Object.keys(x).some((k) => k !== "khao" && k !== "foeKhao") ? 3 : 0), 0); self += (net(e.coin.win) + net(e.coin.lose)) / 2 - a.khao * 0.5; }
    if (e.reviveIlk) self += s.roster.some((r) => r.ruined && ilkOf(r) === e.reviveIlk) ? 12 : 0;
    if (e.ruinNonBeings) self += (at && b.constructs.some((c) => c.side !== f.side && !c.dead && len(c.x - at.x, c.z - at.z) < a.r + c.r)) || (hitP > 0.3 && Object.keys(foe.treasures).length) ? 5 : 0;
    if (e.ruinBenches) { const mine = s.roster.filter((r) => !r.ruined && s.active !== r.idx && !shielded(b, r)).length, theirs = fs.roster.filter((r) => !r.ruined && fs.active !== r.idx && !shielded(b, r)).length; self += theirs > mine ? 20 * (theirs - mine) : -99; }
    if (e.ruinOverLyt) self += lytOf(foe) > e.ruinOverLyt && lyt <= e.ruinOverLyt ? 25 : -99;
    if (e.bothAnima) self += f.anima < foe.anima ? 2 : -2;
    if (e.anima) self += f.anima < f.maxAnima * 0.6 ? 6 : 0;
    if (e.foeKhao) self -= e.foeKhao * 0.3;
  }
  return { score: hurt * hitP + self, at };
}

export function colorOf(f) { return GLOW[f.line] || "#ffffff"; }
const GLOW = {
  spectral_tiger: "#7fe8d8", chaos_dragon: "#ff9b2e", z4z: "#4ae8ff", xulan: "#c9b8ff",
  shadow_fox: "#ffc46a", inverted_serpent: "#ff8aa0", antdroid: "#ffb02e", dg0: "#7fe8d8",
  h01: "#4ac8ff", h09: "#ffb04a", lucky_dragon: "#ffb02e", premier_dragon: "#ffd24a",
};

export function rivalTeam(seed, exclude = []) {
  const R = mulberry32(seed * 7 + 3);
  const pool = Object.keys(LINES).filter((l) => !exclude.includes(l));
  const all = Object.keys(LINES), out = [];
  while (out.length < TEAM_SIZE) {
    const src = pool.length ? pool : all;
    out.push(src.splice(Math.floor(R() * src.length), 1)[0]);
  }
  return out;
}

// for the HUD
export { TIER_LEVEL };
export { ruin as _ruinForTests };   // test hook only
